// Rounding. The one place in the Platform that turns a ratio into a minor unit.
//
// Spec: spec/06-billing-pricing-and-payments.md §1.4 — "Rounding is half-up to the minor unit,
// applied once per line, never on intermediate values."
//
// Two things the spec's three words leave to the implementation, settled here and written down so
// the C# station computes the same paisa:
//
//  1. **Which way a tie goes.** Half-up rounds a tie *away from zero*: 2.5 → 3 and −2.5 → −3. The
//     alternative (always towards +∞) would make `round(−x) ≠ −round(x)`, and a credit note is the
//     negation of the line it reverses (spec/06 §8.7). One paisa of asymmetry there is a ledger
//     that does not balance, which is the invariant this whole package exists to protect.
//  2. **Exactness.** The ratio is evaluated in integer arithmetic, never in floating point:
//     `roundHalfUp(2 675, 100)` is 27, where `Math.round(26.75)` is 27 only by luck and
//     `Math.round(1.005 * 100)` is famously 100. BigInt costs nothing at this size and cannot
//     lose a paisa on a large amount.
import { assertInteger, DomainError } from "../errors.js";
/**
 * `numerator / denominator`, rounded half away from zero, in exact integer arithmetic.
 *
 * Both arguments are whole numbers; the denominator may not be zero and may be negative (the sign
 * moves to the numerator first, so `roundHalfUp(1, -2)` is −1, exactly like `roundHalfUp(-1, 2)`).
 */
export function roundHalfUp(numerator, denominator) {
    assertInteger(numerator, 'numerator');
    assertInteger(denominator, 'denominator');
    const rounded = roundHalfUpBigInt(BigInt(numerator), BigInt(denominator));
    if (rounded > BigInt(Number.MAX_SAFE_INTEGER) || rounded < BigInt(Number.MIN_SAFE_INTEGER)) {
        throw new DomainError('money-out-of-range', `Rounding ${numerator}/${denominator} left the safe integer range.`, {
            numerator,
            denominator,
        });
    }
    return Number(rounded);
}
/** The same rounding on BigInt, for the intermediate values inside this package. */
export function roundHalfUpBigInt(numerator, denominator) {
    if (denominator === 0n) {
        throw new DomainError('division-by-zero', 'Cannot round a ratio with a zero denominator.', {});
    }
    const positiveDenominator = denominator < 0n ? -denominator : denominator;
    const signedNumerator = denominator < 0n ? -numerator : numerator;
    const negative = signedNumerator < 0n;
    const magnitude = negative ? -signedNumerator : signedNumerator;
    // (2|n| + d) / 2d, truncated — which is floor here because every term is non-negative. The +d
    // is what pushes an exact half up to the next unit.
    const quotient = (2n * magnitude + positiveDenominator) / (2n * positiveDenominator);
    return negative ? -quotient : quotient;
}
/** `ceil(numerator / denominator)` for non-negative whole numbers: how PAYG counts billing units. */
export function ceilDiv(numerator, denominator) {
    assertInteger(numerator, 'numerator');
    assertInteger(denominator, 'denominator');
    if (denominator === 0) {
        throw new DomainError('division-by-zero', 'Cannot divide by zero.', {});
    }
    if (numerator < 0 || denominator < 0) {
        throw new DomainError('out-of-range', 'ceilDiv is defined for non-negative whole numbers only.', {
            numerator,
            denominator,
        });
    }
    return Number((BigInt(numerator) + BigInt(denominator) - 1n) / BigInt(denominator));
}
//# sourceMappingURL=round.js.map